// ═══════════════════════════════════════════════════════════════════════
// LES CONDITIONS COMMERCIALES D'AUREUS — UNE SEULE VIE
//
// Demande du fondateur, 2026-08-21 : « si demain le prix passe de 149 € à
// 199 €, je dois pouvoir changer UNE valeur et mettre à jour tout le site. »
//
// POURQUOI CE FICHIER EXISTE
// L'audit du 21/08/2026 a mesuré, sur les 231 pages publiées :
//   · « Frais d'affiliation : 0 € » pour Aureus dans un tableau comparatif
//     (/blog/prix-secretariat-social-belgique) — alors que c'est 149 € ;
//   · un « coût annuel » de 1 740 € présenté comme le total, sans les 149 €,
//     donc faux de 149 € la première année ;
//   · le nombre de commissions paritaires écrit à la main sous SIX valeurs
//     différentes, de 121 à 164.
//
// Le verrou `promesses-interdites.js` ne les avait pas vues, et la raison
// mérite d'être écrite : son motif cherche la négation COLLÉE au substantif
// (« sans frais d'affiliation »). Dans un TABLEAU, l'intitulé et la valeur
// vivent dans deux cellules séparées par du balisage :
//     <td>Frais d'affiliation</td><td>0€</td>
// Aucune expression régulière de proximité textuelle ne peut voir ça. C'est
// pour cette famille-là que le verrou `tarifs-source-unique.test.js` lit les
// tableaux LIGNE PAR LIGNE plutôt qu'en texte suivi.
//
// R1, APPLIQUÉ AUX CHIFFRES COMMERCIAUX
// Les deux montants ne sont pas réécrits ici : ils sont LUS dans
// `catalogue-services.js`, qui reste la source de vérité des prix (GATE B).
// Ce fichier ajoute ce que le catalogue ne porte pas — la durée du contrat,
// le forfait mensuel de base, l'identité légale — et offre les fonctions de
// calcul qui empêchent d'oublier les frais de mise en route.
// ═══════════════════════════════════════════════════════════════════════

import { getPrixService } from '@/app/lib/catalogue-services';
import { NB_CP, NB_CP_REGISTRE } from '@/app/lib/nb-cp';
import { TENANT } from '@/app/lib/tenant-config';

// ───────────────────────────────────────────────────────────────────────
// 1. TARIFS
// ───────────────────────────────────────────────────────────────────────

/**
 * Les quatre conditions commerciales de base.
 *
 * ⚠️ `setupFee` et `payrollSlipPrice` sont DÉRIVÉS du catalogue. Ne jamais y
 * écrire un nombre : le catalogue est la source, ce module est la vitrine.
 */
export const AUREUS_PRICING = {
  /** Frais d'affiliation & implémentation — une seule fois, au démarrage. */
  setupFee: getPrixService('onboarding'),
  /** Prix HTVA d'une fiche de paie traitée. */
  payrollSlipPrice: getPrixService('paie'),
  /**
   * Forfait mensuel de base, hors module complémentaire payant.
   *
   * ⚠️ 0 € ne veut PAS dire « gratuit » ni « sans frais d'affiliation ». Ces
   * deux phrases ont été trouvées en ligne et sont fausses : la mise en route
   * reste due. Écrire « sans forfait mensuel », jamais « sans frais ».
   */
  monthlyBaseFee: 0,
  /** Durée du contrat de services, en années. */
  contractDurationYears: 3,
  /** Devise et régime de TVA de tous les montants ci-dessus. */
  currency: 'EUR',
  vatRegime: 'HTVA',
};

/** « 149 € », « 29 € »… — formatage belge, virgule décimale, espace insécable. */
export function euros(montant) {
  const n = Number(montant);
  if (!Number.isFinite(n)) return null;
  const s = Number.isInteger(n) ? String(n) : n.toFixed(2).replace('.', ',');
  // ⚠️ `\u00a0` ÉCHAPPÉ, PAS TAPÉ (25/08/2026). L'espace insécable avant le
  // symbole € est VOULU — c'est la typographie belge — mais écrit littéralement
  // il déclenche `no-irregular-whitespace` et bloquait tout le CI. L'échapper
  // garde le rendu au caractère près ET rend l'intention lisible : un espace
  // invisible dans le code se lit comme une faute de frappe.
  return `${s}\u00a0€`;
}

/**
 * Le coût Aureus d'une année, frais de mise en route compris la première.
 *
 * C'est LA fonction qui manquait. Le tableau de
 * /blog/prix-secretariat-social-belgique annonçait « coût annuel : 1 740 € »
 * pour 5 travailleurs — soit 5 × 29 × 12, exact — mais présenté comme le
 * total, alors que la première année porte aussi les 149 €. Un frais
 * découvert après coup est ce qui a fait perdre le dossier Solinas
 * le 28/07/2026.
 *
 * @param {number} nbFiches  fiches de paie traitées par mois
 * @param {{premiereAnnee?: boolean, mois?: number}} [opts]
 * @returns {{fiches:number, mois:number, coutFiches:number, setup:number, total:number}|null}
 */
export function coutAnnuel(nbFiches, opts = {}) {
  if (nbFiches === null || nbFiches === undefined || nbFiches === '') return null;
  const n = Number(nbFiches);
  if (!Number.isFinite(n) || n < 0) return null;
  const mois = Number.isFinite(Number(opts.mois)) ? Number(opts.mois) : 12;
  const premiereAnnee = opts.premiereAnnee !== false; // par défaut : la 1re année
  const coutFiches = n * AUREUS_PRICING.payrollSlipPrice * mois;
  const setup = premiereAnnee ? AUREUS_PRICING.setupFee : 0;
  return {
    fiches: n,
    mois,
    coutFiches,
    setup,
    total: coutFiches + setup + AUREUS_PRICING.monthlyBaseFee * mois,
  };
}

/** Coût mensuel courant, hors mise en route (années suivantes). */
export function coutMensuel(nbFiches) {
  // ⚠️ `Number(null)` vaut 0 et `Number('')` aussi : sans ce filtre, une donnée
  // ABSENTE devient « 0 € », c'est-à-dire une affirmation. On rend null, jamais
  // zéro — même règle que partout ailleurs dans le dépôt.
  if (nbFiches === null || nbFiches === undefined || nbFiches === '') return null;
  const n = Number(nbFiches);
  if (!Number.isFinite(n) || n < 0) return null;
  return n * AUREUS_PRICING.payrollSlipPrice + AUREUS_PRICING.monthlyBaseFee;
}

/**
 * La phrase d'engagement, à écrire telle quelle.
 *
 * ⚠️ Ne JAMAIS la remplacer par une négation de durée : le contrat de services
 * est conclu pour trois ans, et le nier est la faute qui a coûté le dossier
 * Solinas le 28/07/2026. Le verrou `offre-lancement-retrait` bannit ces
 * tournures de tout `app/` — y compris, à juste titre, des commentaires.
 */
export const PHRASE_ENGAGEMENT = `Contrat de services de ${AUREUS_PRICING.contractDurationYears} ans`;

/** La phrase de mise en route, à écrire telle quelle. */
export const PHRASE_MISE_EN_ROUTE =
  `Frais d'affiliation & implémentation : ${euros(AUREUS_PRICING.setupFee)} HTVA, une seule fois`;

// ───────────────────────────────────────────────────────────────────────
// 2. CE QUE « TOUT INCLUS » VEUT DIRE
//
// Le site écrit « tout inclus » partout. La formule n'est défendable que si
// l'on peut dire ce qu'elle recouvre ET ce qu'elle ne recouvre pas. Les
// options payantes existent au catalogue (`facturable: true`) : les taire
// derrière « tout inclus » est exactement le frais découvert qu'on s'interdit.
// ───────────────────────────────────────────────────────────────────────

/** Ce que couvre le prix par fiche, sans supplément. */
export const INCLUS_DANS_LA_FICHE = [
  'Calcul de paie complet (ONSS, précompte professionnel, cotisation spéciale)',
  'Fiche de paie PDF',
  'Déclaration Dimona IN/OUT',
  'Déclaration DmfA trimestrielle',
  'Fiches fiscales Belcotax',
  'Pécule de vacances simple et double',
  'Prime de fin d’année selon la commission paritaire',
  'Portail employé',
  'Exports comptables',
  'Virements SEPA',
  'Mise à jour des barèmes sectoriels',
];

/**
 * Ce qui se facture EN PLUS, et qui doit être nommé chaque fois que
 * « tout inclus » est écrit à côté.
 */
export const OPTIONS_PAYANTES = [
  'Règlement de travail et dépôt',
  'Sortie d’un travailleur (C4, décompte, pécule, Dimona OUT)',
  'Formation à l’application',
  'Prestations d’audit et de conseil',
  'Modules complémentaires souscrits séparément',
];

// ───────────────────────────────────────────────────────────────────────
// 3. COMMISSIONS PARITAIRES — quatre nombres, quatre questions différentes
//
// Le site en annonçait six valeurs (121, 128, 130+, 160, 164) parce que
// personne n'avait écrit ce que « CP supportée » voulait dire. Mesure du
// registre au 21/08/2026, reproductible :
//   · 203 entrées au registre (commissions ET sous-commissions) ;
//   · 107 commissions principales distinctes ;
//   · 128 avec un barème sectoriel vérifié ;
//   · 121 avec une page publique /baremes (vérifié + minimum codé + nom) ;
//   ·  75 pour lesquelles le SPF ne publie AUCUN barème sectoriel — le
//        minimum applicable y est le RMMMG national.
//
// Le chiffre qu'on ANNONCE est celui du barème vérifié : c'est le seul qui
// corresponde à une promesse tenable (« votre barème sectoriel est à jour »).
// ───────────────────────────────────────────────────────────────────────

/** Le nombre à écrire sur le site. Commissions au barème sectoriel vérifié. */
export const SUPPORTED_CP_COUNT = NB_CP;

/** Entrées au registre, sous-commissions et fiches documentaires comprises. */
export const REGISTERED_CP_COUNT = NB_CP_REGISTRE;

/** Ce que la promesse recouvre — à citer quand on annonce le chiffre. */
export const DEFINITION_CP_SUPPORTEE =
  'commissions et sous-commissions paritaires dont le barème sectoriel est ' +
  'vérifié à la source officielle, daté, et appliqué automatiquement au calcul de paie';

// ───────────────────────────────────────────────────────────────────────
// 4. IDENTITÉ LÉGALE
//
// ⚠️ Aucune de ces valeurs n'est déduite. Elles sont reprises des mentions
// légales publiées et de la fiche BCE. Le numéro d'entreprise vit déjà dans
// `societe-interne.js` (chiffres seuls) : il est repris ici sous sa forme
// affichable, pas redéfini.
// ───────────────────────────────────────────────────────────────────────

export const COMPANY = {
  /** Dénomination et forme juridique. La SPRL a été abolie par le CSA. */
  legalName: 'Aureus IA SRL',
  legalForm: 'SRL',
  /** Numéro d'entreprise (BCE) et numéro de TVA — identiques en Belgique. */
  companyNumber: 'BE 1028.230.781',
  vatNumber: 'BE 1028.230.781',
  /** Siège social. Adresse mise à jour le 12/06/2026 (Broodthaers → Plasky). */
  street: 'Square Eugène Plasky 92-94 bte 11',
  postalCode: '1030',
  city: 'Schaerbeek',
  region: 'Bruxelles',
  country: 'BE',
  /** Adresse de contact publique. */
  email: TENANT.emailFrom,
  /**
   * Statut au sens des instructions administratives ONSS.
   *
   * ⚠️ Aureus n'a PAS l'agrément « secrétariat social agréé » et ne doit
   * jamais employer cette dénomination — elle est réservée aux agréés. Un
   * prestataire de services peut établir et transmettre Dimona et DmfA pour
   * ses clients ; il ne peut pas percevoir leurs cotisations.
   */
  onssRole: 'Prestataire de services (mandataire non agréé)',
};

/** L'adresse du siège sur une ligne, telle qu'elle s'affiche partout. */
export const COMPANY_ADDRESS_LINE =
  `${COMPANY.street}, ${COMPANY.postalCode} ${COMPANY.city}, ${COMPANY.region}`;

/** La ligne d'identification complète, pour un pied de page ou un document. */
export const COMPANY_IDENTITY_LINE =
  `${COMPANY.legalName} — BCE ${COMPANY.companyNumber} — ${COMPANY_ADDRESS_LINE}`;

// ───────────────────────────────────────────────────────────────────────
// 5. IDENTIFIANTS DÉCLARATIFS
//
// ⚠️ CORRECTION DU 22/08/2026. Ce fichier a d'abord affirmé que le numéro ONSS,
// la référence Mahis et l'identifiant Peppol « n'existaient pas dans le dépôt ».
// C'était faux, et c'est exactement le piège que R2 ter décrit : un premier
// `grep` filtrait les résultats contenant « test », « mock » ou « demo », et il
// a écarté les vraies occurrences avec les fausses. Une recherche qui ne trouve
// rien ne prouve pas l'absence — elle prouve que le motif était mauvais.
// ───────────────────────────────────────────────────────────────────────

/**
 * Matricule employeur d'Aureus IA SRL auprès de l'ONSS.
 * Déjà imprimé sur les avis de paiement ONSS que nous produisons — ce n'est
 * pas un secret, c'est une mention obligatoire.
 */
export const ONSS_REFERENCE = '66999-83';

/**
 * Identifiant Peppol pour la facturation électronique.
 * Schéma 0208 = numéro d'entreprise belge : il se DÉRIVE du BCE, il ne s'invente
 * pas et ne se recopie pas.
 */
export const PEPPOL_ID = `0208:${COMPANY.companyNumber.replace(/\D/g, '')}`;

/**
 * Le mandat ONSS se gère dans l'application Mahis (instructions administratives
 * ONSS, page « Le mandat »). Il n'y a pas de « numéro Mahis » : c'est le canal,
 * pas un identifiant. À ne pas inventer parce qu'une liste en réclamait un.
 */
export const CANAL_MANDAT_ONSS = 'Mahis (application ONSS)';

/** La ligne d'identification déclarative, telle qu'elle figure sur nos avis. */
export const COMPANY_DECLARATIVE_LINE =
  `${COMPANY.legalName} · BCE ${COMPANY.companyNumber} · ONSS ${ONSS_REFERENCE}`;

// ═══════════════════════════════════════════════════════════════════════
// 6. LA DISPONIBILITÉ — LE CHIFFRE DU CONTRAT, PAS CELUI DE L'AFFICHE
// ───────────────────────────────────────────────────────────────────────
// Mesuré le 22/08/2026, et c'est la MÊME maladie que le nombre de CP :
// un seul fait, quatre valeurs différentes selon la page.
//
//   · le CONTRAT s'engage à 99,5 %  — CGV art. 7 (`app/legal/cgv/page.js`,
//     a7_p1) et CGU employé § 7, dans les 4 langues, soit 8 écritures ;
//   · la page fiduciaires vendait « SLA 99,9 % GARANTI »       → 12 écritures ;
//   · la page de campagne affichait « 99,97 % Uptime »         → 12 écritures ;
//   · l'écran de connexion affichait « Frankfurt · 99,99 % SLA » → 4 écritures.
//
// 28 promesses commerciales AU-DESSUS de ce que le contrat engage. Et le
// « 99,9 % garanti » est le pire des trois : c'est mot pour mot la clause
// `a7_p2` RETIRÉE des CGV le 12/08/2026 — retirée précisément pour qu'aucun
// client ne puisse l'exiger. La page de vente continuait à la promettre.
//
// ⚠️ POURQUOI ON NE « MESURE » PAS POUR TRANCHER. La tentation serait de
// chercher le vrai taux de disponibilité et d'afficher celui-là. Non : un
// taux mesuré est un CONSTAT du passé, il change chaque mois et personne ne
// peut l'opposer à Aureus. Ce qu'un prospect lit sur une page de vente, il
// le comprend comme un ENGAGEMENT. Le seul chiffre honnête est donc celui
// qu'on s'engage à tenir — celui du contrat. Il n'y en a qu'un.
//
// Source : contrat de services, art. 7 « Niveau de service (SLA) ».
// ═══════════════════════════════════════════════════════════════════════

/** Le taux de disponibilité ENGAGÉ par le contrat, en pourcentage. */
export const SLA_DISPONIBILITE_PCT = 99.5;

/**
 * Le taux tel qu'il s'écrit — VIRGULE décimale (français, néerlandais,
 * allemand). Ne jamais l'écrire à la main : `99.5%` en dur est à la fois une
 * faute de typographie belge et une valeur qui ne suivra pas le contrat.
 */
export const SLA_DISPONIBILITE = `${String(SLA_DISPONIBILITE_PCT).replace('.', ',')} %`;

/** Idem pour un texte anglais, où la décimale s'écrit avec un point. */
export const SLA_DISPONIBILITE_EN = `${SLA_DISPONIBILITE_PCT}%`;

/**
 * La formulation à utiliser sur toute surface commerciale. Elle dit d'où
 * vient le chiffre : un engagement contractuel, pas une mesure de terrain.
 */
export const PHRASE_SLA = `Disponibilité contractuelle ${SLA_DISPONIBILITE}`;
